import {
  activateWorkflowTemplateVersion,
  useWorkflowTemplate,
} from '@commenda/api';
import { IWorkflowTemplateVersion } from '@commenda/types';
import { useCallback, useState } from 'react';

import { useNotification } from '../../hooks/useNotification';
import { WorkflowVersionActivationHelper } from './workflow-version-activation-helper';

/** Making a version live, for the one publish popover every workflow editor shows. Reads the
 * template alongside the version because the warnings and the live state live on the template. */
export function useWorkflowVersionActivation(
  version: IWorkflowTemplateVersion,
  onVersionChanged?: () => void
) {
  const { setNotification } = useNotification();
  const {
    data,
    isLoading,
    error,
    mutate: refreshTemplate,
  } = useWorkflowTemplate(version.workflowTemplateId);
  const [isActivating, setIsActivating] = useState(false);
  const template = data?.workflowTemplate ?? null;

  const activate = useCallback(async () => {
    setIsActivating(true);
    try {
      const response = await activateWorkflowTemplateVersion(
        version.workflowTemplateId,
        version.id
      );
      setNotification(
        response.ok
          ? {
              show: true,
              valid: true,
              title: 'Workflow live',
              message: 'New runs now use this version.',
            }
          : {
              show: true,
              valid: false,
              title: 'Error',
              message:
                WorkflowVersionActivationHelper.ACTIVATION_FAILURE_MESSAGE,
            }
      );
    } catch {
      setNotification({
        show: true,
        valid: false,
        title: 'Error',
        message: WorkflowVersionActivationHelper.ACTIVATION_FAILURE_MESSAGE,
      });
    }
    await refreshTemplate();
    onVersionChanged?.();
    setIsActivating(false);
  }, [
    version.workflowTemplateId,
    version.id,
    setNotification,
    refreshTemplate,
    onVersionChanged,
  ]);

  return {
    isActive: WorkflowVersionActivationHelper.isActive(version, template),
    isActivating,
    activationWarnings: WorkflowVersionActivationHelper.getActivationWarnings(
      version,
      template
    ),
    activate,
    refreshTemplate,
    activationReadState: WorkflowVersionActivationHelper.getReadState(
      isLoading,
      error,
      template
    ),
    isLoading,
    error,
  };
}
